import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function ValoresServicos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState(user?.empresa_id || "");
  const [tipos, setTipos] = useState([]);
  const [catalogo, setCatalogo] = useState({ nichos: [], servicos_por_nicho: {} });
  const [empresaDoc, setEmpresaDoc] = useState(null);
  const [nichoTab, setNichoTab] = useState("todos");
  const [novoServico, setNovoServico] = useState("");
  const [ativa, setAtiva] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [draft, setDraft] = useState([]); // [{tipo_servico, modo, valor, observacoes}]
  const [observacoes, setObservacoes] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    api.get("/tipos-servico-padrao").then(r => setTipos(r.data));
    api.get("/catalogo/nichos").then(r => setCatalogo(r.data)).catch(() => {});
    if (user?.role === "admin") api.get("/empresas").then(r => setEmpresas(r.data));
    if (user?.role === "empresa") api.get("/minha-empresa").then(r => setEmpresaDoc(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!empresaId) return;
    api.get(`/empresas/${empresaId}/tabela-valores`).then(r => setAtiva(r.data));
    api.get(`/empresas/${empresaId}/tabelas-valores`).then(r => setHistorico(r.data));
    if (user?.role === "admin") setEmpresaDoc(empresas.find(e => e.id === empresaId) || null);
  }, [empresaId, empresas]);

  const NICHO_LABEL = { moveis: "Móveis", estofados: "Estofados", colchoes: "Colchões" };
  const nichosEmpresa = empresaDoc?.nichos?.length ? empresaDoc.nichos : (catalogo.nichos || []);
  const usaMontadora = empresaDoc?.usa_montadora !== false; // só oculta quando explicitamente NÃO

  const servicosDoNicho = (n) => [
    ...(catalogo.servicos_por_nicho[n] || []),
    ...((empresaDoc?.servicos_personalizados || {})[n] || []),
  ];
  const nichoDoServico = (key) => nichosEmpresa.find(n => servicosDoNicho(n).includes(key)) || "outros";

  // Inicializa o draft: serviços dos nichos da empresa + itens já salvos (legado/custom)
  useEffect(() => {
    if (!catalogo.nichos.length && !tipos.length) return;
    const itensAtivos = ativa?.itens || [];
    const map = Object.fromEntries((itensAtivos || []).map(i => [i.tipo_servico, i]));
    const doCatalogo = nichosEmpresa.flatMap(n => servicosDoNicho(n).map(s => ({ key: s, nicho: n })));
    const extras = itensAtivos
      .filter(i => !doCatalogo.some(c => c.key === i.tipo_servico))
      .map(i => ({ key: i.tipo_servico, nicho: nichoDoServico(i.tipo_servico) }));
    const oferecidos = (empresaDoc?.servicos_oferecidos || [])
      .filter(s => !doCatalogo.some(c => c.key === s) && !extras.some(e => e.key === s))
      .map(s => ({ key: s, nicho: nichoDoServico(s) }));
    const vistos = new Set();
    const linhas = [...doCatalogo, ...extras, ...oferecidos].filter(({ key }) => !vistos.has(key) && vistos.add(key));
    setDraft(linhas.map(({ key, nicho }) => ({
      tipo_servico: key,
      nicho,
      label: key.replace(/_/g, " "),
      modo: map[key]?.modo || "percentual",
      valor_montador: map[key]?.valor_montador ?? map[key]?.valor ?? 0,
      valor_montadora: map[key]?.valor_montadora ?? 0,
      observacoes: map[key]?.observacoes || "",
      ativo: map[key] ? map[key]?.ativo !== false : false,
    })));
  }, [tipos, ativa, catalogo, empresaDoc]);

  const updateItem = (key, patch) => {
    setDraft(prev => prev.map(it => it.tipo_servico === key ? { ...it, ...patch } : it));
  };

  const salvar = async () => {
    if (!empresaId) { toast.error("Selecione empresa"); return; }
    const itens = draft
      .filter(d => d.ativo)
      .map(({ tipo_servico, modo, valor_montador, valor_montadora, observacoes }) => ({
        tipo_servico, modo,
        valor_montador: parseFloat(valor_montador) || 0,
        valor_montadora: parseFloat(valor_montadora) || 0,
        observacoes,
      }));
    if (itens.length === 0) { toast.error("Marque ao menos 1 serviço como ativo"); return; }
    // Aviso soft: valor abaixo da REFERÊNCIA MÍNIMA RECOMENDADA MONTAJÁ (não bloqueia)
    try {
      const refs = (await api.get("/referencias-valores")).data.itens || {};
      const abaixo = itens.filter(it => {
        const r = refs[it.tipo_servico];
        return r && r.modo === it.modo && it.valor_montador < r.valor;
      });
      if (abaixo.length > 0) {
        const linhas = abaixo.map(it => {
          const r = refs[it.tipo_servico];
          const fmt = v => it.modo === "percentual" ? `${v}%` : `R$ ${Number(v).toFixed(2)}`;
          return `• ${it.tipo_servico.replace(/_/g, " ")}: informado ${fmt(it.valor_montador)} · referência ${fmt(r.valor)}`;
        }).join("\n");
        const manter = window.confirm(
          `⚠️ ATENÇÃO — VALOR ABAIXO DA REFERÊNCIA DA REDE\n\n${linhas}\n\n` +
          `Valores abaixo da referência podem resultar em menor interesse dos profissionais, ` +
          `menor disponibilidade ou maior tempo para atendimento.\n\n` +
          `OK = MANTER VALOR e salvar · Cancelar = ALTERAR VALOR`);
        if (!manter) return;
      }
    } catch { /* referências indisponíveis não bloqueiam */ }
    setSalvando(true);
    try {
      await api.post(`/empresas/${empresaId}/tabela-valores`, { itens, observacoes });
      toast.success(ativa?.status === "ativa"
        ? "Tabela PROGRAMADA · entra em vigor em 60 dias"
        : "Tabela ATIVA cadastrada");
      const [a, h] = await Promise.all([
        api.get(`/empresas/${empresaId}/tabela-valores`),
        api.get(`/empresas/${empresaId}/tabelas-valores`),
      ]);
      setAtiva(a.data); setHistorico(h.data);
      if (user?.role === "empresa") {
        try {
          const s = (await api.get("/empresa/condicoes-status")).data;
          if (!s.fiscal_ok) {
            toast.info("Próximo passo: Nota Fiscal e dados fiscais");
            navigate("/admin/config-fiscal");
          } else if (!s.calendario_ok) {
            toast.info("Próximo passo: Calendário de fechamento e pagamento");
            navigate("/admin/calendario-fechamento");
          } else {
            toast.info("Configuração completa — publique as condições para liberar o aceite da rede");
            navigate("/admin/condicoes");
          }
        } catch { /* segue na página */ }
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSalvando(false); }
  };

  const temAtiva = ativa?.status === "ativa";

  return (
    <div className="p-8 max-w-5xl">
      <div className="mb-8">
        <div className="label-cap">Configuração · Empresa</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Tabela de Valores por Serviço</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Defina o pagamento de cada tipo de serviço, com valores separados para <strong>Montadores</strong> e <strong>Montadoras</strong> (cada um é pago separadamente).
          <strong> Alterações de valores em tabelas vigentes têm aviso prévio de 60 dias.</strong>
        </p>
      </div>

      {user?.role === "admin" && (
        <div className="mb-6">
          <Label className="label-cap">Empresa</Label>
          <Select value={empresaId} onValueChange={setEmpresaId}>
            <SelectTrigger className="mt-2 max-w-md" data-testid="val-empresa"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {empresas.map(e => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Status da tabela */}
      {empresaId && (
        <div className={`mb-6 p-4 border-l-4 ${temAtiva ? "border-[#00A859] bg-[#00A859]/5" : "border-[#FFCC00] bg-[#FFCC00]/10"}`}
          data-testid="val-status">
          {temAtiva ? (
            <div className="text-sm">
              <strong>Tabela vigente desde</strong> {new Date(ativa.vigencia_inicio).toLocaleDateString("pt-BR")} · versão {ativa.versao}
            </div>
          ) : (
            <div className="text-sm">
              <strong>Nenhuma tabela cadastrada.</strong> Defina os valores abaixo e clique em Salvar. Esta primeira tabela entra em vigor imediatamente.
            </div>
          )}
        </div>
      )}

      {/* Editor */}
      {empresaId && (
        <div className="border border-border bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b border-border bg-[#F4F4F5]">
            <div className="flex gap-1.5">
              {["todos", ...nichosEmpresa].map(n => (
                <button key={n} onClick={() => setNichoTab(n)}
                  className={`px-3 py-1 rounded text-xs font-bold ${nichoTab === n ? "bg-[#142766] text-white" : "border border-border text-muted-foreground hover:border-[#142766]"}`}
                  data-testid={`val-tab-${n}`}>
                  {n === "todos" ? "Todos" : (NICHO_LABEL[n] || n)}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={novoServico} onChange={e => setNovoServico(e.target.value)}
                placeholder="Cadastrar novo serviço" className="h-8 text-xs w-52" data-testid="val-novo-servico" />
              <Button size="sm" variant="outline" className="h-8" data-testid="val-add-servico"
                onClick={() => {
                  const key = novoServico.trim().toLowerCase().replace(/\s+/g, "_");
                  if (!key) return;
                  if (draft.some(d => d.tipo_servico === key)) { toast.info("Serviço já existe na tabela"); return; }
                  setDraft(prev => [...prev, {
                    tipo_servico: key, nicho: nichoTab === "todos" ? (nichosEmpresa[0] || "outros") : nichoTab,
                    label: key.replace(/_/g, " "), modo: "percentual",
                    valor_montador: 0, valor_montadora: 0, observacoes: "", ativo: true,
                  }]);
                  setNovoServico("");
                }}>
                + Adicionar
              </Button>
            </div>
          </div>
          {!usaMontadora && (
            <div className="px-3 py-2 text-[11px] text-muted-foreground border-b border-border">
              Sua empresa marcou que <strong>não trabalha com Montadora</strong> — a coluna de valor de montadora foi ocultada.
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-[#F4F4F5] border-b border-border">
              <tr>
                <th className="text-left p-3 label-cap font-medium w-8">Ativo</th>
                <th className="text-left p-3 label-cap font-medium">Serviço</th>
                <th className="text-left p-3 label-cap font-medium w-24">Nicho</th>
                <th className="text-left p-3 label-cap font-medium w-36">Modo</th>
                <th className="text-left p-3 label-cap font-medium w-36">Valor Montador</th>
                {usaMontadora && <th className="text-left p-3 label-cap font-medium w-36">Valor Montadora</th>}
                <th className="text-left p-3 label-cap font-medium">Observações</th>
              </tr>
            </thead>
            <tbody>
              {draft.filter(it => nichoTab === "todos" || it.nicho === nichoTab).map(it => (
                <tr key={it.tipo_servico} className="border-b border-border last:border-0" data-testid={`val-row-${it.tipo_servico}`}>
                  <td className="p-3">
                    <input type="checkbox" checked={it.ativo}
                      onChange={(e) => updateItem(it.tipo_servico, { ativo: e.target.checked })}
                      data-testid={`val-ativo-${it.tipo_servico}`} />
                  </td>
                  <td className="p-3 font-medium capitalize">{it.label}</td>
                  <td className="p-3 text-xs text-muted-foreground">{NICHO_LABEL[it.nicho] || it.nicho}</td>
                  <td className="p-3">
                    <Select value={it.modo} onValueChange={(v) => updateItem(it.tipo_servico, { modo: v })}>
                      <SelectTrigger className="h-9" data-testid={`val-modo-${it.tipo_servico}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentual">% sobre produto</SelectItem>
                        <SelectItem value="fixo">R$ fixo</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground">{it.modo === "percentual" ? "%" : "R$"}</span>
                      <Input type="number" step="0.01" min="0" value={it.valor_montador}
                        onChange={(e) => updateItem(it.tipo_servico, { valor_montador: e.target.value })}
                        className="h-9" data-testid={`val-valor-montador-${it.tipo_servico}`} />
                    </div>
                  </td>
                  {usaMontadora && (
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground">{it.modo === "percentual" ? "%" : "R$"}</span>
                        <Input type="number" step="0.01" min="0" value={it.valor_montadora}
                          onChange={(e) => updateItem(it.tipo_servico, { valor_montadora: e.target.value })}
                          className="h-9" data-testid={`val-valor-montadora-${it.tipo_servico}`} />
                      </div>
                    </td>
                  )}
                  <td className="p-3">
                    <Input value={it.observacoes}
                      onChange={(e) => updateItem(it.tipo_servico, { observacoes: e.target.value })}
                      className="h-9 text-xs" data-testid={`val-obs-${it.tipo_servico}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="p-4 border-t border-border bg-[#F4F4F5]">
            <Label className="label-cap">Observações gerais da tabela</Label>
            <Textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)}
              rows={2} className="mt-2 text-sm" data-testid="val-obs-geral" />
          </div>

          <div className="p-4 border-t border-border flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              {temAtiva
                ? "⚠️ Salvar criará uma tabela PROGRAMADA com vigência em D+60 (aviso prévio obrigatório aos montadores)."
                : "✅ Esta é a primeira tabela e entrará em vigor imediatamente."}
            </div>
            <Button onClick={salvar} disabled={salvando}
              className="btn-sharp brand-bg text-white hover:bg-[#001A5E]" data-testid="val-salvar">
              {salvando ? "Salvando…" : temAtiva ? "Programar nova tabela (D+60)" : "Salvar tabela vigente"}
            </Button>
          </div>
        </div>
      )}

      {/* Histórico */}
      {empresaId && historico.length > 0 && (
        <div className="mt-8">
          <h2 className="font-heading font-bold text-xl mb-3">Histórico de tabelas</h2>
          <div className="border border-border bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F4F5] border-b border-border">
                <tr>
                  <th className="text-left p-3 label-cap font-medium">Versão</th>
                  <th className="text-left p-3 label-cap font-medium">Status</th>
                  <th className="text-left p-3 label-cap font-medium">Vigência</th>
                  <th className="text-left p-3 label-cap font-medium">Itens</th>
                  <th className="text-left p-3 label-cap font-medium">Criada por</th>
                </tr>
              </thead>
              <tbody>
                {historico.map(h => (
                  <tr key={h.id} className="border-b border-border last:border-0" data-testid={`hist-${h.id}`}>
                    <td className="p-3 font-mono">#{h.versao}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${
                        h.status === "ativa" ? "bg-[#00A859] text-white"
                        : h.status === "programada" ? "bg-[#FFCC00]"
                        : "bg-secondary text-muted-foreground"
                      }`}>{h.status}</span>
                    </td>
                    <td className="p-3 text-xs">
                      {new Date(h.vigencia_inicio).toLocaleDateString("pt-BR")}
                      {h.vigencia_fim ? ` → ${new Date(h.vigencia_fim).toLocaleDateString("pt-BR")}` : " (em diante)"}
                    </td>
                    <td className="p-3">{(h.itens || []).length}</td>
                    <td className="p-3 text-xs">{h.criada_por}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
